// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, {
    cartPageTemplateBreakpoint,
    CartPageTemplateStyles,
} from "@flip/App/Components/Cart/CartPageTemplate";
import CheckoutMoveToStep from "@flip/App/Components/Checkout/CheckoutMoveToStep";
import SectionWrapper, { SectionWrapperStyles } from "@flip/App/Components/Common/SectionWrapper";
import XngZone from "@flip/App/Components/Common/XngZone";
import Notify from "@flip/App/Components/Notify";
import XngSticky from "@flip/App/Components/XngSticky";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { CheckoutStepper } from "@flip/App/Widgets/CheckoutStepper/CheckoutStepper";
import { CHECKOUT_STEPS } from "@flip/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@flip/App/Widgets/CheckoutStepper/StepperContext";
import { color, xScreen } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showCheckoutStepper = "showCheckoutStepper",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showCheckoutStepper]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        isPlacingOrder: state.pages.checkoutReviewAndSubmit.isPlacingOrder,
        // XNG-Changes: FLIP-984
        isPreloadingData:
            state.pages.orderConfirmation.isPreloadingData ||
            state.pages.checkoutShipping.isPreloadingData ||
            state.pages.checkoutReviewAndSubmit.isPreloadingData,
        isLoading: cartState?.isLoading,
        isChangingAddress: (state as CustomAppState).xCheckout.isChangingShipToAddress,
        shipToState: getShipToState(state, cartState.value ? cartState.value.shipToId : undefined),
        billToState: getBillToState(state, cartState.value?.billToId),
        countries: getCurrentCountries(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CheckoutPageContainerStyles extends CartPageTemplateStyles {
    headerTitle?: TypographyPresentationProps;
    cartLinesSection?: InjectableCss;
    sectionWrapper?: SectionWrapperStyles;
    loadingOverlay?: LoadingOverlayProps;
}

export const checkoutPageContainerStyles: CheckoutPageContainerStyles = {
    container: {
        css: css`
            min-height: calc(100vh - 185px);
            margin-bottom: 0;
        `,
    },
    headerTitle: {
        variant: "h1",
        css: css`
            margin-bottom: 0;
            ${xScreen("lte", "md")} {
                margin-bottom: 8px;
            }
            padding-top: 16px;
        `,
    },
    headerSection: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            width: 100%;
            padding: 0 0 12px 0;
            flex-wrap: wrap;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 24px;

            ${xScreen("lte", "md")} {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    totalSection: {
        css: css`
            width: 319px;

            ${xScreen("lte", "md")} {
                width: 234px;
            }

            ${xScreen("lte", "sm")} {
                margin-top: 22px;
            }

            ${xScreen("lte", 767)} {
                width: 100%;
                display: flex;
                margin-top: 32px;
                justify-content: center;

                ${({ currentStep }: any) => currentStep === 4 && `display:none;`}
                & > div.xng-sticky {
                    max-width: 100%;
                    width: 100%;
                }
            }
        `,
    },
    cartLinesSection: {
        css: css`
            width: 100%;

            @media print {
                width: 100%;
            }

            [data-test-selector="cardLinesList_current"] {
                margin-top: 0;
                > div:first-of-type {
                    display: none;
                }
            }
        `,
    },
    // XNG-Changes: FLIP-818
    sectionWrapper: {
        title: {
            wrapper: {
                css: css`
                    margin-bottom: 18px;
                    ${xScreen("lte", "md")} {
                        border-bottom: 1px solid ${getColor("common.border")};
                        padding: 12px;
                    }
                    ${xScreen("lte", "sm")} {
                        flex-direction: row;
                        align-items: baseline;
                    }
                `,
            },
            title: {
                variant: "h3",
                css: css`
                    ${xScreen("lte", "sm")} {
                        margin-right: 32px;
                    }
                `,
            },
        },
    },
    actionsSection: {
        css: css`
            display: flex;
            align-items: flex-end;
        `,
    },
    loadingOverlay: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = checkoutPageContainerStyles;

const CheckoutPageContainer: FC<Props> = ({
    id,
    fields: { title, showCheckoutStepper },
    isPlacingOrder,
    isLoading,
    isChangingAddress,
    shipToState,
    billToState,
    countries,
    isPreloadingData,
}) => {
    const {
        currentStep,
        checkoutInfoState: { showSpinner },
    } = useContext(StepperContext);
    const isReview = currentStep === CHECKOUT_STEPS.REVIEW;

    // XNG-Changes: FLIP-984
    return (
        <LoadingOverlay
            {...styles.loadingOverlay}
            loading={
                !countries ||
                isPlacingOrder ||
                isLoading ||
                isChangingAddress ||
                showSpinner ||
                !shipToState ||
                !billToState ||
                billToState.isLoading ||
                shipToState.isLoading ||
                isPreloadingData
            }
        >
            <CartPageTemplate isReview={isReview} extendedStyles={styles}>
                <CartPageTemplate.MainSection>
                    <CartPageTemplate.HeaderSection>
                        {title && <Typography {...styles.headerTitle}>{title}</Typography>}
                        {showCheckoutStepper && <CheckoutStepper />}
                    </CartPageTemplate.HeaderSection>
                    <XngZone contentId={id} zoneName="Content0" hint="Zone For Header (Cart Approval message)" />
                    <XngZone
                        contentId={id}
                        zoneName="Content1"
                        hint="Zone For Step Content (Shipping container, Submit & Review container)"
                    />
                    {currentStep === CHECKOUT_STEPS.REVIEW && (
                        <StyledWrapper {...styles.cartLinesSection}>
                            <SectionWrapper
                                title={translate("Cart Items")}
                                extraChild={<CheckoutMoveToStep moveToStep={CHECKOUT_STEPS.CART} />}
                                extendedStyles={styles.sectionWrapper}
                            >
                                {createWidgetElement("CheckoutReviewAndSubmit/CheckoutReviewAndSubmitProductList", {
                                    fields: {},
                                })}
                            </SectionWrapper>
                        </StyledWrapper>
                    )}
                </CartPageTemplate.MainSection>
                <StyledWrapper {...({ currentStep } as any)} {...styles.totalSection}>
                    <XngSticky isCheckoutPge={true}>
                        <XngZone contentId={id} zoneName="Content2" hint="Zone For Checkout Total" />
                    </XngSticky>
                </StyledWrapper>

                <CartPageTemplate.ActionsSection>
                    <XngZone contentId={id} zoneName="Content3" hint="Zone For Stepper Actions" />
                </CartPageTemplate.ActionsSection>
            </CartPageTemplate>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutPageContainer),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Page Container",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "General",
                displayName: "Title",
                defaultValue: "Title",
            },
            {
                name: fields.showCheckoutStepper,
                displayName: "Show Checkout Stepper",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
            },
        ],
    },
};

export default widgetModule;
